import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { usePreferences } from '#/pages/Settings/hooks/usePreferences';
import { Path } from '#/service/routes';
import { authActions, authStore } from '#/store/auth/auth.store';

/**
 * Global hook that determines whether to show the preferences prompt on page load / navigation.
 * Only evaluates once per pathname — not when the flag changes mid-flow (e.g. after registration).
 * Follows the same pattern as `useTermsModal`.
 */
export const usePreferencesPrompt = () => {
  const { pathname } = useLocation();

  const {
    multiSelectInput,
    selectedPreferences,
    isPreferencesLoading,
    isPreferencesUpdating,
    handleUpdatePreferences,
  } = usePreferences();

  const [showPrompt, setShowPrompt] = useState(false);
  const checkedPathname = useRef<string>();

  useEffect(() => {
    if (isPreferencesLoading || checkedPathname.current === pathname) {
      return;
    }

    checkedPathname.current = pathname;

    const shouldShow =
      !pathname.startsWith(Path.SettingsRoot) &&
      authStore.shouldPromptPreferences &&
      !!multiSelectInput &&
      !selectedPreferences.length;

    if (shouldShow) {
      setShowPrompt(true);
      authActions.setShouldPromptPreferences(false);
    }
  }, [isPreferencesLoading, multiSelectInput, selectedPreferences, pathname]);

  const handleClose = useCallback(() => {
    setShowPrompt(false);
  }, []);

  return {
    showPrompt,
    multiSelectInput,
    selectedPreferences,
    isPreferencesUpdating,
    handleUpdatePreferences,
    handleClose,
  };
};
